import i18next from 'i18next'

import Log from 'src/lib/loggingUtil'
import Snackbar from 'src/lib/Snackbar'
import {isDev} from 'src/lib/utils/environmentUtil'
import {ErrorMessageLookup, isValidGraphQLError} from 'src/lib/errors/errors.types'
import EventStreamSingleton from 'src/lib/EventStream/EventStream'
import {AppEvents, GeneralErrorEvents} from 'src/lib/Analytics/app_events'
import {LogLevels} from 'src/config'

const UnknownErrorString = 'Unknown Error'
const genericErrorLocalizationKey = 'Common:GenericError'

const loadErrorsNamespace = async (): Promise<void> => {
  await i18next.loadNamespaces('Errors')
}

/**
 * Logs the given error. Will log the default error message if no error message can be found. Does not display the error message to the user.
 * @param e Error to log, if possible.
 * @param message Additional context. Will always be included in the log message
 */
export const LogException = (e: unknown, message?: string): void => {
  if (e === undefined || e === null) {
    Log.error(UnknownErrorString, message)
    return
  }

  if (e instanceof Error) {
    Log.error(e, message)
    return
  }

  if (typeof e === 'string') {
    Log.error(e, message)
    return
  }

  Log.error(UnknownErrorString, message)
}

loadErrorsNamespace().catch(LogException)

type GetErrorMessageFromErrorReturnType = [string | undefined, boolean]

/**
 * Attempts to get an error message out of any type of error object
 * Warning! In some cases this function displays the raw error response from the server to the user!
 * We really shouldn't be doing this. For new gql errors, add an error code to Errors.json instead.
 * Currently only shows custom message for `MGE1001` error Code (See Errors.json localization file)
 * @param e - any type of error object
 * Supports:
 * * javascript Error
 * * gql response
 * * string
 * @returns [message, isDisplayingRawGqlResponse]
 * Where
 * * error is the error message text pulled out of e. If no error message is found, will return undefined
 * * isDisplayingRawGqlResponse is true if the error response is the raw output of a gql error response, otherwise false
 */
export const getErrorMessageFromError = (e: unknown): GetErrorMessageFromErrorReturnType => {
  let err: string
  if (e === undefined || e === null) {
    return [undefined, false]
  } else if (isValidGraphQLError(e) && 'message' in e) {
    err = e.message
    if (i18next.exists(`Errors:${err}`)) {
      return [i18next.t(`Errors:${err}`), false]
    } else {
      return [e.message, true]
    }
  } else if (e instanceof Error && 'message' in e) {
    return [e.message, false]
  } else if (e instanceof Error && 'name' in e) {
    return [e.name, false]
  } else if (typeof e === 'string') {
    return [e, false]
  } else {
    return [undefined, false]
  }
}

/**
 * Log an error if we are displaying the server response
 * @param title
 * @param context
 */
const logInstanceOfDisplayingRawGqlResponseToUser = (title: string, context: string = ''): void => {
  EventStreamSingleton.emit({
    type: 'analytic',
    name: GeneralErrorEvents.gql_response_displayed_to_user,
    category: AppEvents.Category.GeneralError,
    data: {
      title,
      context,
    },
    destinations: [], // should go only to DD
  })
}

/**
 * Given an error, show the snackbar with the given error message.
 * This displays the raw message from the error to the user.
 * If you don't have a specific error message to display to the user, use logErrorAndShowException instead.
 * Due to javascript catch limitations all errors are caught as `any`
 * @param e
 * @deprecated use logErrorAndShowException instead
 **/
export function ShowException(e: unknown): void {
  const title = getErrorMessageFromError(e)[0]
  Snackbar.error({
    title: title ?? UnknownErrorString,
    duration: Snackbar.LENGTH_LONG,
  })
}

/**
 * Given an error, show the snackbar with the given error message, and log the error message
 * This displays the raw message from the error to the user.
 * If you don't have a specific error message to display to the user, use logErrorAndShowException instead.
 * @param e
 * @deprecated use logErrorAndShowException instead
 **/
export const displayAndLogError = (
  e: unknown,
  context?: string,
  logLevel: LogLevels = LogLevels.Error,
): void => {
  if (logLevel === LogLevels.Error) {
    Log.error(e, context ?? '')
  } else {
    Log.write(logLevel, e, context ?? '')
  }
  const [title, isDisplayingGqlResponse] = getErrorMessageFromError(e)
  if (title && isDisplayingGqlResponse) {
    logInstanceOfDisplayingRawGqlResponseToUser(title, context)
  }
  ShowException(e)
}

export const ContextualizedLogException = (context: string): ((e: unknown) => void) => {
  return (e: unknown): void => {
    LogException(e, context)
  }
}

/**
 * Displays generic error message in Snackbar and logs the given error.
 * It prevents error message to show in production, but shows in dev environment.
 * @param e
 */
export const logErrorAndShowException = async (
  err: unknown,
  context?: string,
  logLevel: LogLevels = LogLevels.Error,
): Promise<void> => {
  if (logLevel === LogLevels.Error) {
    Log.error(err, context ?? '')
  } else {
    Log.write(logLevel, err, context ?? '')
  }

  const defaultErrorMessage = i18next.t(genericErrorLocalizationKey)

  const isDevEnv = await isDev()

  let errorMessage = defaultErrorMessage
  if (isDevEnv && err instanceof Error) {
    errorMessage = err?.message ?? defaultErrorMessage
  }

  Snackbar.error({
    title: errorMessage,
    duration: Snackbar.LENGTH_LONG,
  })
}

type ShowErrorMessageArguments = {
  error: unknown
  /** localize error message based on the `errorCode`.
   * When this function is not defined or returns `null`, we show the default error message. */
  errorMessageMapper?: ErrorMessageLookup
  context?: string
}

/**
 * Shows the snackbar with a localized error message, unless `errorMessageMapper`
 * is not defined, or `error` doesn't have `extensions.errorCode`
 * In that case it falls back to generic error message and logs the `error` with `context`.
 * @param error
 * @param errorMessageMapper
 * @param context
 */
export const logAndShowErrorMessage = ({
  error,
  errorMessageMapper,
  context,
}: ShowErrorMessageArguments): void => {
  const defaultErrorMessage = i18next.t(genericErrorLocalizationKey)

  let errorMessage = defaultErrorMessage

  if (errorMessageMapper && isValidGraphQLError(error)) {
    const errorCode: string =
      typeof error.extensions.errorCode === 'string' ? error.extensions.errorCode : ''

    const mappedMessage = errorMessageMapper(errorCode)

    // Ensure the mapper does not return the error code itself as the message
    if (mappedMessage === errorCode) {
      if (__DEV__) {
        // eslint-disable-next-line no-console
        console.warn(
          'errorMessageMapper returned with the backend error code. It should not be exposed to the user.\nLocalize the message instead.',
        )
      }
    } else {
      errorMessage = mappedMessage ?? defaultErrorMessage
    }
  }

  Log.error(error, context ?? '')

  Snackbar.error({
    title: errorMessage,
    duration: Snackbar.LENGTH_LONG,
  })
}
